import { useDispatch } from "react-redux";
import ReactHtmlParser from "react-html-parser";

import {
  Button,
  H1,
  H2,
  H3,
  Label,
  MultilineInput,
  Text,
  Tooltip,
} from "@salt-ds/core";
import { InfoSolidIcon } from "@salt-ds/icons";

import CheckBoxGroupComponent from "src/components/ChechBoxGroupComponent";
import InfoBannerComponent from "src/components/InfoBannerComponent";

import { submitFeedback } from "src/reducers/slices/FeedBackSlice";

import useAchIntroductionViewModel from "./useAchIntroductionViewModel";

import { CONTINUE_TO_ACH_ENROLLMENT_BTN } from "src/utils/Constants";
import { hasAllowedSpecialChars } from "src/utils/AchEnrollment";

import { isRequired as isEmpty } from "../../../utils/UtilityFunction";

import { HAS_INVALID_SPECIAL_CHARS } from "src/constants/achEnrollment";

const MAX_CHARACTERS = 256;

const AchIntroductionView = ({
  viewModel: achEnrollmentViewModel,
}: {
  viewModel: any;
}) => {
  const dispatch = useDispatch();
  const viewModel = useAchIntroductionViewModel();

  const getCheckBoxOptions = () => {
    const { features = [] } = viewModel?.ACHData?.content?.overview || {};

    const checkboxOptions = features.map((feature: any) => ({
      label: (
        <label htmlFor={feature.mid}>
          {feature.content}{" "}
          {feature.tooltip && (
            <Tooltip
              className="rounded w-full"
              content={ReactHtmlParser(feature.tooltip)}
              placement="right"
            >
              <InfoSolidIcon
                tabIndex={0}
                className=" text-tooltipBlue font-bold "
              />
            </Tooltip>
          )}
        </label>
      ),
      value: feature.mid,
    }));

    return checkboxOptions;
  };

  // -- handlers -- //

  const handleCheckBoxChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    // prettier-ignore
    const { setSelectedOptions, setDisableOtherUserInput, setAdditionalElement, setSpecialCharError } = viewModel;

    const { value, checked } = event.target;

    if (value === "f6") {
      const isDisableOtherInput = checked ? false : true;

      setDisableOtherUserInput(isDisableOtherInput);

      if (isDisableOtherInput) {
        setAdditionalElement("");
        setSpecialCharError(false);
      }
    }

    setSelectedOptions((prev) =>
      checked ? [...prev, value] : prev.filter((val) => val !== value)
    );
  };

  const handleOtherInputChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { setAdditionalElement, setIsError, setSpecialCharError } = viewModel;

    const newVal = event.target.value;
    setAdditionalElement(newVal);
    setIsError(newVal.length > MAX_CHARACTERS);

    // prettier-ignore
    const isSpecialCharError = !isEmpty(newVal) && !hasAllowedSpecialChars(newVal);

    setSpecialCharError(isSpecialCharError);
  };

  const handleContinueClick = () => {
    const { selectedOptions, additionalElement } = viewModel;
    const { setAchQuestionsSubmitted } = achEnrollmentViewModel;

    const { overview } = viewModel?.ACHData?.content || {};

    const { features = [] } = overview || {};

    const feedback = selectedOptions.map((option) => {
      const feature = features.find((f) => f.mid === option);
      if (option === "f6" && additionalElement.trim()) {
        return `${additionalElement.trim()}`;
      }
      return feature?.content || "";
    });

    dispatch(submitFeedback({ feedback: feedback }));

    setAchQuestionsSubmitted(true);
  };

  // -- renders -- //

  const renderPageInfo = () => {
    const { ACHData } = viewModel;

    const heading =
      ACHData?.content?.overview?.conteDataDetails[0]?.contentData;

    const subHeading =
      ACHData?.content?.overview?.conteDataDetails[1]?.contentData;

    const content =
      ACHData?.content?.overview?.conteDataDetails[3]?.contentData;

    return (
      <div className="bg-white flex-col justify-start items-start gap-6 inline-flex">
        <div className="pr-[396px] justify-start items-center inline-flex">
          <H1>{heading}</H1>
        </div>
        <div className=" justify-start items-start gap-2 inline-flex">
          <H2 className="text-black/80 text-lg font-semibold font-['Open Sans'] leading-normal">
            {subHeading}
          </H2>
        </div>
        <Text className="w-[684px]"> {ReactHtmlParser(content)}</Text>
      </div>
    );
  };

  const renderQuestion = () => {
    const mainQuestion =
      viewModel?.ACHData?.content?.overview?.conteDataDetails[2]?.contentData;

    return (
      <div className="justify-start items-start mt-[40px]">
        <div>
          <H3>
            {mainQuestion}
            <span className="text-[#e32b16]">*</span>
          </H3>
        </div>
      </div>
    );
  };

  const renderQuestionOptions = () => {
    const { selectedOptions } = viewModel;
    const checkboxOptions = getCheckBoxOptions();

    return (
      <div className="flex-col justify-start items-center inline-flex mt-[16px]">
        <CheckBoxGroupComponent
          options={checkboxOptions}
          checkedValues={selectedOptions}
          onChange={handleCheckBoxChange}
        />
      </div>
    );
  };

  const renderOtherErrorMessages = () => {
    const { additionalElement, isError, isSpecialCharError } = viewModel;

    // prettier-ignore
    if (isSpecialCharError) {
      return <strong>{HAS_INVALID_SPECIAL_CHARS}</strong>;
    }
    else if (isError) {
      return <strong>{`${additionalElement.length}/${MAX_CHARACTERS} characters`}</strong>
    }
  };

  const renderOtherPref = () => {
    const {
      additionalElement,
      isError,
      isSpecialCharError,
      disableOtherUserInput,
    } = viewModel;

    return (
      <MultilineInput
        id="f6"
        aria-label="textbox area"
        className="rounded ml-[22px] pl-[4px] w-[389px] mt-[4px]"
        bordered
        disabled={disableOtherUserInput}
        value={additionalElement}
        onChange={handleOtherInputChange}
        endAdornment={
          <Label variant={!isError ? "secondary" : "primary"}>
            {!isError && !isSpecialCharError && (
              <strong className="text-black">{`${additionalElement.length}/${MAX_CHARACTERS} characters`}</strong>
            )}
            {renderOtherErrorMessages()}
          </Label>
        }
        validationStatus={isSpecialCharError || isError ? "error" : undefined}
      />
    );
  };

  const renderInfoBanner = () => {
    const bannerNote =
      viewModel?.ACHData?.content?.overview?.conteDataDetails[4]?.contentData;

    return (
      <InfoBannerComponent
        className="w-[661px] mt-[40px] rounded"
        message={ReactHtmlParser(bannerNote)}
      />
    );
  };

  const renderContinueButton = () => {
    // prettier-ignore
    const { selectedOptions, additionalElement, isSpecialCharError } = viewModel;

    const isButtonDisabled =
      selectedOptions.length === 0 ||
      (selectedOptions.includes("f6") && additionalElement.trim() === "") ||
      isSpecialCharError ||
      additionalElement.length > MAX_CHARACTERS;

    return (
      <Button
        disabled={isButtonDisabled}
        onClick={handleContinueClick}
        variant="cta"
        className="bg-customBlue rounded normal-case mt-[40px]"
      >
        {CONTINUE_TO_ACH_ENROLLMENT_BTN}
      </Button>
    );
  };

  return (
    <div className="flex items-center justify-center">
      <div className="flex-col justify-start items-start inline-flex mt-[35px]">
        {renderPageInfo()}
        {renderQuestion()}
        {renderQuestionOptions()}
        {renderOtherPref()}
        {renderInfoBanner()}
        {renderContinueButton()}
      </div>
    </div>
  );
};

export default AchIntroductionView;
